/**
 * Route wrapper that gates a field page on the permission hook.
 *
 * Behavior:
 *   - Unauthenticated → redirect to /field/login, remembering the target URL
 *     so we can bounce the user back after login.
 *   - Authenticated but not permitted → redirect to /field/home with a toast.
 *   - Loading → render a tiny inline spinner; we don't unmount the prior page.
 *
 * Today everything resolves to `allowed: true` for authenticated users, so the
 * "not permitted" branch never fires. It's wired up now so flipping to
 * role-based gating later only changes the hook, not the page components.
 */

import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { notifications } from '../../../config/toast-notifications.js'

import { usePagePermission, type FieldPageKey } from './usePagePermission.js'

interface RequirePermissionProps {
  pageKey: FieldPageKey
  children: ReactNode
}

export function RequirePermission({ pageKey, children }: RequirePermissionProps) {
  const location = useLocation()
  const { allowed, isLoading, isUnauthenticated } = usePagePermission(pageKey)

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 text-sm text-muted-foreground">
        Checking permissions…
      </div>
    )
  }

  if (isUnauthenticated) {
    return <Navigate to="/field/login" state={{ from: location.pathname + location.search }} replace />
  }

  if (!allowed) {
    notifications.info('Not available', "You don't have access to that page.")
    return <Navigate to="/field/home" replace />
  }

  return <>{children}</>
}
